import React, { ReactNode } from 'react';

// Utils
import { client } from '../../../tina/__generated__/client';

// Types
import { PartnerDataModel } from '@brz-types/partner.data.model';
import { useReferralParams } from './referral.helper';
import { usePage } from './page.context';



type PartnerContextModel = {
    partner: PartnerDataModel | null;
    setPartner: React.Dispatch<React.SetStateAction<PartnerDataModel | null>>;
    updatePartnerData: (prop: keyof PartnerDataModel, value: any) => void;
};

const PartnerContext = React.createContext({} as PartnerContextModel)


export const PartnerContextWrapper = ({ children }: { children: ReactNode | ReactNode[] }) => {
    const { page: settings } = usePage();

    const [pid, setPid] = React.useState<string | null>(settings?.partner?.partnerRef?.pid || null);
    const [partner, setPartner] = React.useState<PartnerDataModel | null>(null);

    // Cookies
    const referral = useReferralParams();

    // On Mount: Get URL pid
    React.useEffect(() => {
        if ( typeof window !== 'undefined' ) {
            const params = new URLSearchParams(location.search);
            const queryPid = params.get('pid');

            if ( !settings?.partner?.partnerRef?.pid ) {
                setPid(queryPid);
            }
        }
    }, []);


    // On marketing pid change
    React.useEffect(() => {
        const marketingPartnerId = referral?.marketingPartnerId;

        if ( typeof window !== 'undefined' && !settings?.partner?.partnerRef?.pid && marketingPartnerId && marketingPartnerId !== pid ) {
            setPid(marketingPartnerId);
        }
    }, [referral?.marketingPartnerId]);


    // Fetch partner data on pid change
    React.useEffect(() => {
        if ( typeof window !== 'undefined' && pid ) {
            fetchPartner(pid).then(data => {
                setPartner(data);
            });
        }
    }, [pid]);


    React.useEffect(() => {
        const marketingPartnerId = referral?.marketingPartnerId;

        // console.log(referral, partner, marketingPartnerId);

        if ( typeof window !== 'undefined' && !!referral && !!partner && (!marketingPartnerId || marketingPartnerId !== partner.pid) ) {
            referral.updateParam('marketingPartnerId', partner.pid);
        }
    }, [partner, referral]);


    const updatePartnerData = (prop: keyof PartnerDataModel, value: any) => {
        setPartner(Object.assign({}, partner || {} as PartnerDataModel, {
            [prop]: value
        }))
    };


    return (
        <PartnerContext.Provider value={{
            partner,
            setPartner,
            updatePartnerData
        }}>
            { children }
        </PartnerContext.Provider>
    )
};

export const usePartner = () => React.useContext(PartnerContext);



const fetchPartner = async (pid: string | number) => {
    const { data } = await client.request({
        query: `
        query FindPartnersWithPid($pid: String) {
            partnerConnection(filter: { pid: { eq: $pid }, enabled: { eq: true } }) {
                edges {
                    node {
                        pid
                        title
                        logo {
                            src
                        }
                        displayName
                        ctaLink
                        products
                        disclaimer
                    }
                }
            }
        }
    `,
        variables: {
            pid: typeof pid === 'number' ? pid.toString() : pid
        }
    });

    if ( data.partnerConnection.edges?.[0]?.node ) {
        return data.partnerConnection.edges[0].node;
    }

    return null;
}